import { useMemo } from "react";
import { calcRequiredFTE, calcEffectiveHours } from "../../utils/calculations";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function calcMonthFTE(monthData, stages, eff, volume) {
  const vol = monthData.volume ?? volume;
  const localProd = monthData.local_prod ?? 80;
  const localUtil = monthData.local_util ?? 75;
  const expatProd = monthData.expat_prod ?? 80;
  const expatUtil = monthData.expat_util ?? 75;

  let total = 0;
  for (const s of stages) {
    const lc = s.local_count || 0;
    const ec = s.expat_count || 0;
    const tc = lc + ec;
    if (tc === 0) continue;
    const sv = vol * ((s.yield_pct || 100) / 100);
    const blendedProd = (lc * localProd + ec * expatProd) / tc;
    const blendedHrs = (lc * eff.monthlyHrsLocal + ec * eff.monthlyHrsExpat) / tc;
    const blendedUtil = (lc * localUtil + ec * expatUtil) / tc;
    const tmn = sv * (s.aht || 10) / Math.max(blendedProd / 100, 0.01);
    const amp = blendedHrs * 60 * Math.max(blendedUtil / 100, 0.01);
    total += amp > 0 ? tmn / amp : 0;
  }
  return total;
}

export default function DemandPlannerTab({ project, updateDemand, effectiveHours }) {
  const setup = project?.setup || {};
  const stages = project?.stages || [];
  const vc = setup.volume_capacity || {};
  const demand = project?.demand || { monthly_data: MONTHS.map((_, i) => ({ month: i, volume: 1000, local_util: 75, local_prod: 80, expat_util: 75, expat_prod: 80 })), next_year_delta: 10 };
  const monthlyData = demand.monthly_data || [];
  const nextYearDelta = demand.next_year_delta ?? 10;
  const baseVolume = vc.total_monthly_volume || 1000;
  const eff = effectiveHours || calcEffectiveHours(setup.effective_hours);

  const monthFTEs = useMemo(() => {
    return monthlyData.map((md) => calcMonthFTE(md, stages, eff, baseVolume));
  }, [monthlyData, stages, eff, baseVolume]);

  const nextYearFTEs = useMemo(() => {
    return monthlyData.map((md, i) => {
      const nextVol = (md.volume ?? baseVolume) * (1 + nextYearDelta / 100);
      return calcMonthFTE({ ...md, volume: nextVol }, stages, eff, baseVolume);
    });
  }, [monthlyData, nextYearDelta, stages, eff, baseVolume]);

  const updateMonth = (idx, key, val) => {
    const updated = monthlyData.map((m, i) => i === idx ? { ...m, [key]: val } : m);
    updateDemand({ ...demand, monthly_data: updated });
  };

  const setNextYearDelta = (v) => updateDemand({ ...demand, next_year_delta: v });

  const peakFTE = Math.max(...monthFTEs);
  const troughFTE = Math.min(...monthFTEs);
  const peakIdx = monthFTEs.indexOf(peakFTE);
  const troughIdx = monthFTEs.indexOf(troughFTE);

  const chartData = MONTHS.map((m, i) => ({
    month: m,
    current: parseFloat(monthFTEs[i]?.toFixed(1) || 0),
    nextYear: parseFloat(nextYearFTEs[i]?.toFixed(1) || 0),
  }));

  // Stage × Month heatmap
  const stageMonthFTEs = useMemo(() => {
    return stages.map((s) => ({
      name: s.stage_name,
      ftesPerMonth: monthlyData.map((md) => calcMonthFTE(md, [s], eff, baseVolume)),
    }));
  }, [stages, monthlyData, eff, baseVolume]);

  const getHeatColor = (val, minVal, maxVal) => {
    if (maxVal === minVal) return "#A3B8A5";
    const pct = (val - minVal) / (maxVal - minVal);
    if (pct >= 0.8) return "#4B5E4D";
    if (pct >= 0.4) return "#8FA891";
    return "#C5D6C7";
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 6</p>
        <h2 className="font-heading text-2xl font-semibold text-sage-900">Annual Demand Planner</h2>
        <p className="text-sage-500 text-sm mt-1">Model seasonal demand across 12 months. Adjust per-month volume and productivity to reconcile with actuals.</p>
      </div>

      {/* Monthly cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        {MONTHS.map((month, idx) => {
          const md = monthlyData[idx] || {};
          const currFTE = monthFTEs[idx] || 0;
          const nextFTE = nextYearFTEs[idx] || 0;
          const delta = nextFTE - currFTE;
          return (
            <div key={month} className="bg-white border border-[#E2E8E3] rounded-md p-3" data-testid={`month-card-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading text-sm font-semibold text-sage-800">{month}</span>
                <div className="text-right">
                  <div className="font-heading text-base font-bold text-sage-900">{currFTE.toFixed(1)}</div>
                  <div className={`font-heading text-xs font-semibold ${delta > 0 ? "text-amber-600" : delta < 0 ? "text-green-600" : "text-sage-400"}`}>{delta >= 0 ? "+" : ""}{delta.toFixed(1)} YY</div>
                </div>
              </div>
              <div className="mb-2">
                <label className="text-[10px] text-sage-500 font-semibold uppercase tracking-wider">Volume</label>
                <input type="number" value={md.volume ?? baseVolume} onChange={(e) => updateMonth(idx, "volume", Number(e.target.value))} min={0} className="w-full border border-sage-100 rounded px-2 py-1 text-xs mt-0.5 focus:border-sage-400 outline-none" />
              </div>
              <div className="space-y-1.5">
                {[
                  { key: "local_util", label: "L Util" },
                  { key: "local_prod", label: "L Prod" },
                  { key: "expat_util", label: "E Util" },
                  { key: "expat_prod", label: "E Prod" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span className="text-[10px] text-sage-400 w-10 flex-shrink-0">{label}</span>
                    <input type="range" min={40} max={130} value={md[key] ?? 75} onChange={(e) => updateMonth(idx, key, Number(e.target.value))} className="flex-1 h-1 cursor-pointer" />
                    <span className="text-[10px] text-sage-600 font-semibold w-7 text-right">{md[key] ?? 75}%</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Next year simulation */}
      <div className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-semibold text-sage-800">Next Year Volume Simulation</h3>
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-bold text-sage-900">{nextYearDelta >= 0 ? "+" : ""}{nextYearDelta}%</span>
          </div>
        </div>
        <div className="mb-4">
          <input
            data-testid="next-year-delta-slider"
            type="range" min={-30} max={50} value={nextYearDelta}
            onChange={(e) => setNextYearDelta(Number(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-sage-400"><span>-30%</span><span>0%</span><span>+50%</span></div>
        </div>
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { label: "Peak Month (Current)", value: `${peakFTE.toFixed(1)} FTE`, sub: MONTHS[peakIdx] },
            { label: "Trough Month (Current)", value: `${troughFTE.toFixed(1)} FTE`, sub: MONTHS[troughIdx] },
            { label: "Peak Month (Next Year)", value: `${Math.max(...nextYearFTEs).toFixed(1)} FTE`, sub: MONTHS[nextYearFTEs.indexOf(Math.max(...nextYearFTEs))] },
            { label: "Avg Delta vs Current", value: `${(nextYearFTEs.reduce((a, b) => a + b, 0) / 12 - monthFTEs.reduce((a, b) => a + b, 0) / 12).toFixed(1)} FTE` },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-sage-50 rounded-md p-3 text-center">
              <p className="text-xs text-sage-500 mb-1">{label}</p>
              <p className="font-heading text-lg font-bold text-sage-900">{value}</p>
              {sub && <p className="text-xs text-sage-400">{sub}</p>}
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} margin={{ top: 0, right: 10, bottom: 0, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAF0EA" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8FA891" }} />
            <YAxis tick={{ fontSize: 10, fill: "#8FA891" }} />
            <Tooltip contentStyle={{ borderColor: "#E2E8E3", borderRadius: 6, fontSize: 12 }} />
            <Bar dataKey="current" name="Current Year FTE" fill="#7C9A7E" radius={[3, 3, 0, 0]} />
            <Bar dataKey="nextYear" name="Next Year FTE" fill="#D97706" radius={[3, 3, 0, 0]} opacity={0.7} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Heat map */}
      {stages.length > 0 && (
        <div className="bg-white border border-[#E2E8E3] rounded-md p-5 overflow-x-auto">
          <h3 className="font-heading text-base font-semibold text-sage-800 mb-4">Stage × Month FTE Heat Map</h3>
          <table className="w-full min-w-max text-xs">
            <thead>
              <tr className="bg-sage-50">
                <th className="text-left py-2 px-3 text-sage-500 font-semibold uppercase tracking-wider">Stage</th>
                {MONTHS.map((m) => <th key={m} className="py-2 px-2 text-center text-sage-500 font-semibold uppercase tracking-wider">{m}</th>)}
                <th className="py-2 px-2 text-center text-sage-500 font-semibold uppercase tracking-wider">Peak</th>
                <th className="py-2 px-2 text-center text-sage-500 font-semibold uppercase tracking-wider">Trough</th>
              </tr>
            </thead>
            <tbody>
              {stageMonthFTEs.map((stage, si) => {
                const maxVal = Math.max(...stage.ftesPerMonth);
                const minVal = Math.min(...stage.ftesPerMonth);
                return (
                  <tr key={si} className="border-b border-sage-50">
                    <td className="py-1.5 px-3 text-sage-700 font-medium whitespace-nowrap">{stage.name}</td>
                    {stage.ftesPerMonth.map((fte, mi) => (
                      <td key={mi} className="py-1.5 px-2 text-center">
                        <span className="px-1.5 py-0.5 rounded text-white font-semibold text-[10px]" style={{ backgroundColor: getHeatColor(fte, minVal, maxVal) }}>
                          {fte.toFixed(1)}
                        </span>
                      </td>
                    ))}
                    <td className="py-1.5 px-2 text-center font-semibold text-sage-800">{maxVal.toFixed(1)}</td>
                    <td className="py-1.5 px-2 text-center font-semibold text-sage-400">{minVal.toFixed(1)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center gap-4 mt-3 text-xs text-sage-400">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded inline-block bg-[#4B5E4D]" /> High demand (top 20%)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded inline-block bg-[#8FA891]" /> Mid demand</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded inline-block bg-[#C5D6C7]" /> Low demand</span>
          </div>
        </div>
      )}
    </div>
  );
}
